import { Spinner } from "@shared/components/ui-kit/feedback/Spinner/Spinner.web";
import zIndexes from "@shared/theme/props/zIndexes";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { CSSProperties, useEffect, useState } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import type { ProcessingLoaderSharedProps } from "./ProcessingLoader.props";

export default function ProcessingLoaderContainer({
  containerRef,
  opacity = 1,
  refSelector,
}: ProcessingLoaderContainerProps): JSX.Element {
  const { t } = useTranslation("checkout");
  const [overlayStyle, setOverlayStyle] = useState({});

  // This effect calculates and updates the overlay position
  // to match only the visible portion of the container as the user scrolls.
  // Useful for when the container is a modal.
  useEffect(() => {
    if (!containerRef?.current) {
      return undefined;
    }

    const container = containerRef.current;

    const updatePosition = () => {
      const rect = refSelector
        ? document.querySelector(refSelector)?.getBoundingClientRect()
        : container.getBoundingClientRect();

      const visibleTop = Math.max(0, rect?.top || 0);
      const visibleBottom = Math.min(window.innerHeight, rect?.bottom || 0);
      const visibleHeight = visibleBottom - visibleTop;

      setOverlayStyle({
        top: `${visibleTop}px`,
        height: `${visibleHeight}px`,
        width: `${rect?.width}px`,
        left: `${rect?.left}px`,
        position: "fixed",
      });
    };

    updatePosition();

    container.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);

    return () => {
      container.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [containerRef]);

  return (
    <Box
      opacity={opacity}
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      backgroundColor="bg.subtle"
      zIndex={zIndexes.zIndexMax}
      flex={1}
      width="100%"
      height="100%"
      {...(containerRef?.current ? overlayStyle : {})}
    >
      <Box display="flex" height="100%">
        <Flex
          direction="column"
          width="full"
          height="full"
          align="center"
          justify="center"
          gap="xl"
        >
          <Text fontSize="xl" fontWeight={700}>
            {t("Processing your order")}
          </Text>
          <Spinner size="2xl" thickness="4px" />
        </Flex>
      </Box>
    </Box>
  );
}

export type ProcessingLoaderContainerProps = ProcessingLoaderSharedProps & {
  // Add any web specific props here.
  opacity?: CSSProperties["opacity"];
  refSelector?: string;
};
